/**
 * Cluster 5 — Screen L : Trading P&L.
 *
 * Layout:
 *   - KPI strip (Pool exposure now / Cap book MW / Cap book strike avg / Generation MWh / Realised $/MWh)
 *   - Cap book vs expected gen — 12m forward chart (bars + line) + coverage table
 *   - Pool exposure today — per-DUID table with MW × spot
 *   - Causer-pays per region — table of FCAS_REGU_USAGE_FACTORS
 *   - Revenue attribution per MWh — stacked progress-bar list
 *
 * Data: GET /api/portfolios/<id>/trading
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, Stack, Skeleton, Alert, Chip, Tooltip,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import axios from 'axios';

import KpiCard, { fmtAUDCompact, fmtMWhCompact, fmtAUDPerMWh } from './KpiCard';
import CommercialWorkspaceTabs from './CommercialWorkspaceTabs';
import { useThemeColors } from '../theme/ThemeModeContext';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const COMPONENT_COLOR = {
  PPA_FIXED: '#D98A2B',
  POOL:      '#E9B065',
  FCAS:      '#3b82f6',
  LGC:       '#22c55e',
  CAUSER:    '#ef4444',
};

function fmtMonth(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
}

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-AU', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function fmtSigned(v, fmt = (x) => fmtAUDCompact(Math.abs(x))) {
  if (v == null) return '—';
  return v >= 0 ? `+${fmt(v)}` : `−${fmt(v)}`;
}


// ── Per-MWh stacked bar ──────────────────────────────────────────────────

function StackedHorizontalBar({ components, colors }) {
  const total = components.reduce((s, c) => s + Math.max(c.aud_per_mwh, 0), 0) || 1;
  return (
    <Box sx={{
      display: 'flex', height: 28, borderRadius: 1, overflow: 'hidden',
      border: '1px solid', borderColor: 'divider', mb: 1,
    }}>
      {components.filter((c) => c.aud_per_mwh > 0).map((c) => (
        <Tooltip key={c.key} title={`${c.label}: A$${c.aud_per_mwh.toFixed(2)}/MWh`} arrow>
          <Box sx={{
            width: `${c.aud_per_mwh / total * 100}%`,
            bgcolor: COMPONENT_COLOR[c.key] || colors.textMute,
            opacity: 0.85,
            transition: 'width 240ms ease',
          }} />
        </Tooltip>
      ))}
    </Box>
  );
}

function ComponentLegend({ comp, colors }) {
  const color = COMPONENT_COLOR[comp.key] || colors.textMute;
  const isNeg = comp.aud_per_mwh < 0;
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        <FiberManualRecordIcon sx={{ fontSize: 9, color }} />
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {comp.label}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1.25} alignItems="baseline" sx={{ flexShrink: 0 }}>
        <Typography sx={{
          fontWeight: 600, fontSize: '0.88rem',
          color: isNeg ? '#ef4444' : 'text.primary',
          fontVariantNumeric: 'tabular-nums',
        }}>
          A${comp.aud_per_mwh.toFixed(2)}/MWh
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary',
                                            minWidth: 50, textAlign: 'right',
                                            fontVariantNumeric: 'tabular-nums' }}>
          {comp.pct.toFixed(1)}%
        </Typography>
      </Stack>
    </Stack>
  );
}


// ── Page ─────────────────────────────────────────────────────────────────

export default function TradingPnL() {
  const { portfolioId } = useParams();
  const colors = useThemeColors();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/portfolios/${portfolioId}/trading`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [portfolioId]);

  // Cap book overlay chart prep
  const { capXs, expectedMWh, capMWh, capStrike } = useMemo(() => {
    const arr = data?.cap_book_overlay || [];
    return {
      capXs:       arr.map((p) => new Date(p.period_start)),
      expectedMWh: arr.map((p) => Number(((p.expected_mwh || 0) / 1000).toFixed(1))),  // GWh
      capMWh:      arr.map((p) => Number(((p.cap_contracted_mwh || 0) / 1000).toFixed(1))),
      capStrike:   arr.map((p) => p.cap_avg_strike || 0),
    };
  }, [data]);

  const totalCapBookMW = (data?.cap_book || []).reduce((s, c) => s + (c.contracted_mw || 0), 0);
  const wAvgCapStrike = (() => {
    const cap = data?.cap_book || [];
    if (!cap.length) return null;
    const num = cap.reduce((s, c) => s + (c.contracted_mw || 0) * (c.strike_aud_per_mwh || 0), 0);
    const den = cap.reduce((s, c) => s + (c.contracted_mw || 0), 0);
    return den > 0 ? num / den : null;
  })();

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
      <CommercialWorkspaceTabs />
      {/* Header */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em' }}>
          Trading P&L
        </Typography>
        {data?.portfolio?.name && (
          <Chip label={data.portfolio.name} size="small"
                sx={{ bgcolor: 'primary.main', color: 'background.paper',
                      fontWeight: 600, fontSize: '0.72rem', height: 22 }} />
        )}
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {data?.period?.label || '—'}
        </Typography>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      {/* KPI strip */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
        gap: 1.5, mb: 2,
      }}>
        <KpiCard
          label="Pool exposure now" loading={loading}
          value={data?.pool_exposure?.exposure_aud_per_hour != null
            ? `${fmtAUDCompact(data.pool_exposure.exposure_aud_per_hour)}/h` : '—'}
          unit={data?.pool_exposure?.total_mw != null ? `· ${data.pool_exposure.total_mw.toFixed(0)} MW` : ''}
          accent="#E9B065"
          tooltip={data?.pool_exposure?.timestamp ? `As of ${fmtTime(data.pool_exposure.timestamp)}` : 'Latest dispatch interval × spot RRP.'}
        />
        <KpiCard
          label="Cap book" loading={loading}
          value={data?.cap_book?.length ? `${totalCapBookMW.toFixed(0)} MW` : '—'}
          unit={data?.cap_book?.length ? `· ${data.cap_book.length} contract${data.cap_book.length === 1 ? '' : 's'}` : ''}
          tooltip="Sum of contracted MW across active Cap contracts."
        />
        <KpiCard
          label="Cap avg strike" loading={loading}
          value={wAvgCapStrike != null ? fmtAUDPerMWh(wAvgCapStrike) : '—'}
          tooltip="MW-weighted average strike across the cap book."
        />
        <KpiCard
          label="Generation 30d" loading={loading}
          value={data?.per_mwh?.generation_mwh != null
            ? fmtMWhCompact(data.per_mwh.generation_mwh) : '—'}
          tooltip="Total dispatch MWh across the portfolio over the trailing 30 days."
        />
        <KpiCard
          label="Realised A$/MWh" loading={loading}
          value={data?.per_mwh?.total_aud_per_mwh != null
            ? `A$${data.per_mwh.total_aud_per_mwh.toFixed(2)}` : '—'}
          tooltip="Sum of all per-MWh revenue components (Pool + PPA uplift + FCAS + LGC + Causer-pays)."
        />
      </Box>

      {/* Cap book vs expected gen 12m forward */}
      <Card sx={{ p: 2.25, mb: 2 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            Cap book vs expected generation — 12m forward
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            GWh / month
          </Typography>
        </Stack>
        {loading ? (
          <Skeleton variant="rectangular" height={260} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
        ) : capXs.length === 0 ? (
          <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.disabled">
              No forward generation budget — seed financial_budget for this portfolio.
            </Typography>
          </Box>
        ) : (
          <BarChart
            height={280}
            xAxis={[{
              data: capXs.map((d) => fmtMonth(d.toISOString())),
              scaleType: 'band',
              tickLabelStyle: { fill: colors.textDim, fontSize: 11 },
            }]}
            yAxis={[{
              tickLabelStyle: { fill: colors.textDim, fontSize: 11 },
              valueFormatter: (v) => `${v} GWh`,
            }]}
            series={[
              { id: 'expected', data: expectedMWh, label: 'Expected gen', color: '#D98A2B',
                valueFormatter: (v) => `${v?.toFixed(1)} GWh` },
              { id: 'cap',      data: capMWh,      label: 'Cap-covered',  color: '#E9B065',
                valueFormatter: (v) => `${v?.toFixed(1)} GWh` },
            ]}
            margin={{ top: 16, right: 24, bottom: 30, left: 64 }}
            slotProps={{ legend: { direction: 'horizontal', position: { vertical: 'top', horizontal: 'center' } } }}
            sx={{
              '& .MuiChartsAxis-line':   { stroke: colors.hairline2 },
              '& .MuiChartsAxis-tick':   { stroke: colors.hairline2 },
              '& .MuiChartsGrid-line':   { stroke: colors.hairline },
              '& .MuiChartsLegend-label':{ fill: colors.textDim, fontSize: '0.75rem' },
            }}
          />
        )}
      </Card>

      {/* Two-up: Pool exposure today + Causer-pays */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: 2, mb: 2 }}>
        {/* Pool exposure now */}
        <Card sx={{ p: 0 }}>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between"
                 sx={{ px: 2.25, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Pool exposure today
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {data?.pool_exposure?.timestamp ? `as of ${fmtTime(data.pool_exposure.timestamp)}` : '—'}
            </Typography>
          </Stack>
          {/* Header */}
          <Box sx={{
            display: 'grid', gap: 1,
            gridTemplateColumns: '1.5fr 1fr 1fr 1.2fr',
            px: 2, py: 1, bgcolor: colors.surface2,
            borderBottom: '1px solid', borderColor: 'divider',
          }}>
            {['DUID','MW','Spot','Exposure'].map((h, i) => (
              <Typography key={i} variant="caption" sx={{
                fontWeight: 600, color: 'text.secondary',
                letterSpacing: '0.06em', textTransform: 'uppercase',
                textAlign: i === 0 ? 'left' : 'right',
              }}>{h}</Typography>
            ))}
          </Box>
          {loading ? (
            <Box sx={{ p: 2 }}>
              {[1,2,3,4].map((i) => (
                <Skeleton key={i} variant="rectangular" height={36} sx={{ bgcolor: colors.skeleton, mb: 1 }} />
              ))}
            </Box>
          ) : !data?.pool_exposure?.by_duid?.length ? (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
              <Typography>No pool exposure snapshot.</Typography>
            </Box>
          ) : (
            data.pool_exposure.by_duid.map((d) => (
              <Box key={d.duid} sx={{
                display: 'grid', gap: 1,
                gridTemplateColumns: '1.5fr 1fr 1fr 1.2fr',
                px: 2, py: 1.1, alignItems: 'center',
                borderBottom: '1px solid', borderColor: 'divider',
                '&:last-child': { borderBottom: 'none' },
              }}>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography sx={{ fontWeight: 600, fontSize: '0.88rem' }}>{d.duid}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace' }}>
                    {d.region}
                  </Typography>
                </Stack>
                <Typography sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '0.88rem',
                                  color: d.mw < 0 ? '#ef4444' : d.mw === 0 ? 'text.disabled' : 'text.primary' }}>
                  {d.mw >= 0 ? '+' : ''}{d.mw.toFixed(0)} MW
                </Typography>
                <Typography sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '0.88rem' }}>
                  A${d.rrp.toFixed(2)}
                </Typography>
                <Typography sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                  fontWeight: 600, fontSize: '0.88rem',
                                  color: d.exposure_aud_per_hour < 0 ? '#ef4444' : 'text.primary' }}>
                  {fmtAUDCompact(d.exposure_aud_per_hour)}/h
                </Typography>
              </Box>
            ))
          )}
        </Card>

        {/* Causer-pays factors */}
        <Card sx={{ p: 0 }}>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between"
                 sx={{ px: 2.25, py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Causer-pays factors per region
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              FCAS_REGU_USAGE_FACTORS
            </Typography>
          </Stack>
          <Box sx={{
            display: 'grid', gap: 1,
            gridTemplateColumns: '1.5fr 1.5fr 1fr 1fr',
            px: 2, py: 1, bgcolor: colors.surface2,
            borderBottom: '1px solid', borderColor: 'divider',
          }}>
            {['Region','Service','Factor','Effective'].map((h, i) => (
              <Typography key={i} variant="caption" sx={{
                fontWeight: 600, color: 'text.secondary',
                letterSpacing: '0.06em', textTransform: 'uppercase',
                textAlign: i === 2 ? 'right' : 'left',
              }}>{h}</Typography>
            ))}
          </Box>
          {loading ? (
            <Box sx={{ p: 2 }}>
              {[1,2,3,4,5].map((i) => (
                <Skeleton key={i} variant="rectangular" height={32} sx={{ bgcolor: colors.skeleton, mb: 1 }} />
              ))}
            </Box>
          ) : !data?.causer_pays?.length ? (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
              <Typography>No factor data.</Typography>
            </Box>
          ) : (
            data.causer_pays.map((cp, i) => (
              <Box key={i} sx={{
                display: 'grid', gap: 1,
                gridTemplateColumns: '1.5fr 1.5fr 1fr 1fr',
                px: 2, py: 0.9, alignItems: 'center',
                borderBottom: '1px solid', borderColor: 'divider',
                '&:last-child': { borderBottom: 'none' },
              }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{cp.region}</Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem',
                                  textTransform: cp.bid_type === 'RAISEREG' ? 'none' : 'none' }}>
                  {cp.bid_type === 'RAISEREG' ? 'Raise Reg' : cp.bid_type === 'LOWERREG' ? 'Lower Reg' : cp.bid_type}
                </Typography>
                <Typography sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                                  fontWeight: 600, fontSize: '0.85rem' }}>
                  {(cp.factor * 100).toFixed(2)}%
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.72rem' }}>
                  {cp.effective_date ? new Date(cp.effective_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                </Typography>
              </Box>
            ))
          )}
        </Card>
      </Box>

      {/* Per-MWh attribution stack */}
      <Card sx={{ p: 2.25 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            Revenue attribution per MWh
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            {data?.per_mwh?.total_aud_per_mwh != null
              ? `Total A$${data.per_mwh.total_aud_per_mwh.toFixed(2)}/MWh`
              : '—'}
          </Typography>
        </Stack>
        {loading ? (
          <Skeleton variant="rectangular" height={120} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
        ) : !data?.per_mwh?.components?.length ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.disabled">No per-MWh data in this period.</Typography>
          </Box>
        ) : (
          <Box>
            <StackedHorizontalBar components={data.per_mwh.components} colors={colors} />
            <Stack spacing={0.25}>
              {data.per_mwh.components.map((c) => (
                <ComponentLegend key={c.key} comp={c} colors={colors} />
              ))}
            </Stack>
            <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                LGC priced at the current spot mid from the warehouse (history to Jun 2025);
                CER REC Registry integration deferred to Cluster 6.
                Causer-pays per-MWh is a placeholder — full per-DUID settlement requires MarketNet.
              </Typography>
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  );
}
